
import { useCollectionDataOnce } from "react-firebase-hooks/firestore";
import { Container } from "reactstrap";
import { firestore } from "./firebase";
import { MediaEntry } from "./Types";
import trennlinie from "./img/line-headline.svg";

const toSpotifyEmbedUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url.trim());
    if (!parsed.hostname.includes("spotify.com")) {
      return null;
    }

    const pathParts = parsed.pathname.split("/").filter(Boolean);

    // Handles URLs like /episode/<id>, /show/<id>, also with locale prefixes like /intl-de/episode/<id>
    const typeIndex = pathParts.findIndex(
      (part) => part === "episode" || part === "show"
    );

    if (typeIndex === -1 || !pathParts[typeIndex + 1]) {
      return null;
    }

    const mediaType = pathParts[typeIndex];
    const mediaId = pathParts[typeIndex + 1];

    return `https://open.spotify.com/embed/${mediaType}/${mediaId}?utm_source=oembed`;
  } catch {
    return null;
  }
};

const toYoutubeEmbedUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.toLowerCase();

    if (host.includes("youtu.be")) {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }

    if (host.includes("youtube.com")) {
      if (parsed.pathname === "/watch") {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }

      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.split("/").filter(Boolean)[1];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }

      if (parsed.pathname.startsWith("/embed/")) {
        const id = parsed.pathname.split("/").filter(Boolean)[1];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const getEmbedUrl = (media: MediaEntry): string | null => {
  if (media.type === "spotify") {
    return toSpotifyEmbedUrl(media.url);
  }

  if (media.type === "youtube") {
    return toYoutubeEmbedUrl(media.url);
  }

  return null;
};

const compareByIndex = (a: MediaEntry, b: MediaEntry): number => {
  const aIndex = a.index ?? Number.MAX_SAFE_INTEGER;
  const bIndex = b.index ?? Number.MAX_SAFE_INTEGER;

  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }

  return a.url.localeCompare(b.url);
};

const Media: React.FC<{ setName: (name: string) => void }> = ({ setName }) => {
  setName("Media");

  const [mediaEntries] = useCollectionDataOnce<MediaEntry>(
    firestore.collection("/media")
  );

  const spotifyEntries = (mediaEntries || [])
    .filter((entry) => entry.type === "spotify")
    .sort(compareByIndex);
  const youtubeEntries = (mediaEntries || [])
    .filter((entry) => entry.type === "youtube")
    .sort(compareByIndex);

  return (
    <Container>
      <h1>
        {""}
        <img src={trennlinie} className="svg-icon svg-baseline " /> Media
      </h1>

      <h2>
        {""}
        <img src={trennlinie} className="svg-icon svg-baseline " /> Podcast
      </h2>

      {spotifyEntries.length === 0 && <p>Keine Podcast-Eintraege vorhanden.</p>}
      {spotifyEntries.map((entry, index) => {
        const embedUrl = getEmbedUrl(entry);
        if (!embedUrl) {
          return null;
        }

        return (
          <div
            key={`spotify-${entry.url}-${index}`}
            style={{
              left: 0,
              width: "100%",
              height: "152px",
              position: "relative",
              marginBottom: "12px",
            }}
          >
            <iframe
              src={embedUrl}
              style={{
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                position: "absolute",
                border: 0,
              }}
              allowFullScreen
              allow="clipboard-write *; encrypted-media *; fullscreen *; picture-in-picture *;"
              title={`spotify-${index}`}
            ></iframe>
          </div>
        );
      })}

      <h2>
        {""}
        <img src={trennlinie} className="svg-icon svg-baseline " /> Youtube
      </h2>

      {youtubeEntries.length === 0 && <p>Keine YouTube-Eintraege vorhanden.</p>}
      {youtubeEntries.map((entry, index) => {
        const embedUrl = getEmbedUrl(entry);
        if (!embedUrl) {
          return null;
        }

        return (
          <div
            key={`youtube-${entry.url}-${index}`}
            style={{ marginBottom: "20px", width: "100%" }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "16 / 9",
              }}
            >
            <iframe
              src={embedUrl}
              title={`youtube-${index}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            ></iframe>
            </div>
          </div>
        );
      })}
    </Container>
  );
};

export default Media;
